/**
 * HUG - User Management Page
 * Invite users, manage permissions, view team
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { 
  Users, UserPlus, Mail, Shield, Settings, Trash2, 
  RefreshCw, Check, X, Clock, ChevronDown, ChevronUp, Edit, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All available sections/permissions
const SECTIONS = [
  { key: 'dashboard', label: 'Dashboard', description: 'Main overview page' },
  { key: 'tasks', label: 'Tasks', description: 'Weekly to-do list' },
  { key: 'live_shows', label: 'Live Shows', description: 'Gigs and tour dates' },
  { key: 'campaigns', label: 'Campaigns', description: 'Marketing campaigns' },
  { key: 'highlights', label: 'Highlights', description: 'PR wins and achievements' },
  { key: 'finance', label: 'Finance', description: 'Financial overview' },
  { key: 'invoices', label: 'Invoices', description: 'Create and manage invoices' },
  { key: 'files', label: 'Files', description: 'File repository' },
  { key: 'news', label: 'News', description: 'News aggregator' },
  { key: 'social_planner', label: 'Social Planner', description: 'Social media scheduling' },
  { key: 'record_label', label: 'Record Label', description: 'Label costs and recoupment' },
  { key: 'artists', label: 'Artists', description: 'Artist management' },
  { key: 'settings', label: 'Settings', description: 'Company settings' },
  { key: 'user_management', label: 'User Management', description: 'Invite and manage users' },
];

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'manager',
    permissions: {}
  });
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    name: '',
    email: '',
    password: '',
    role: ''
  });

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching users:', error);
      }
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view users');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const openEditDialog = (u) => {
    setEditForm({
      id: u.id,
      name: u.name,
      email: u.email,
      password: '', // Don't pre-fill password
      role: u.role
    });
    setEditDialogOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role
      };
      
      // Only include password if it was changed
      if (editForm.password) {
        updateData.password = editForm.password;
      }
      
      await axios.put(`${API}/users/${editForm.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('User updated successfully!');
      setEditDialogOpen(false);
      setEditForm({ id: '', name: '', email: '', password: '', role: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/invite`, inviteForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Invite sent to ${inviteForm.email}!`);
      setInviteDialogOpen(false);
      setInviteForm({ email: '', name: '', role: 'manager', permissions: {} });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/${selectedUser.id}/permissions`, {
        permissions: selectedUser.permissions || selectedUser.effective_permissions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Permissions updated!');
      setPermissionsDialogOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleResendInvite = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/${userId}/resend-invite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invite resent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const confirmDelete = (u) => {
    setUserToDelete(u);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/${userToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const togglePermission = (key) => {
    if (!selectedUser) return;
    
    const currentPerms = selectedUser.permissions || selectedUser.effective_permissions || {};
    setSelectedUser({
      ...selectedUser,
      permissions: {
        ...currentPerms,
        [key]: !currentPerms[key]
      }
    });
  };

  const toggleInvitePermission = (key) => {
    setInviteForm({
      ...inviteForm,
      permissions: {
        ...inviteForm.permissions,
        [key]: !inviteForm.permissions[key]
      }
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-500';
      case 'manager': return 'bg-blue-500/20 text-blue-500';
      case 'artist': return 'bg-green-500/20 text-green-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  const getStatusBadge = (u) => {
    if (!u.invite_accepted) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
    }
    return (
      <span className="flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-500 rounded-full text-xs">
        <Check className="w-3 h-3" />
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Separate users by role
  const admins = users.filter(u => u.role === 'admin');
  const managers = users.filter(u => u.role === 'manager');
  const artists = users.filter(u => u.role === 'artist');

  return (
    <div className="space-y-6 p-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Invite team members and manage permissions</p>
        </div>
        <Button onClick={() => setInviteDialogOpen(true)} className="flex items-center gap-2">
          <UserPlus className="w-4 h-4" />
          Invite User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/20 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-sm text-muted-foreground">Total Users</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{admins.length}</p>
              <p className="text-sm text-muted-foreground">Admins</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{managers.length}</p>
              <p className="text-sm text-muted-foreground">Managers</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <Users className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{artists.length}</p>
              <p className="text-sm text-muted-foreground">Artists</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Admins & Managers */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Team Members
        </h2>
        
        <div className="space-y-3">
          {[...admins, ...managers].map((u) => (
            <div key={u.id} className="border border-border rounded-lg overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                onClick={() => setExpandedUsers({ ...expandedUsers, [u.id]: !expandedUsers[u.id] })}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-lg font-bold">{u.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(u.role)}`}>
                    {u.role}
                  </span>
                  {getStatusBadge(u)}
                  {expandedUsers[u.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              
              {expandedUsers[u.id] && (
                <div className="px-4 pb-4 pt-2 border-t border-border bg-muted/30">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {SECTIONS.filter(s => u.effective_permissions?.[s.key]).map((section) => (
                      <span key={section.key} className="px-2 py-1 bg-green-500/10 text-green-600 rounded text-xs">
                        {section.label}
                      </span>
                    ))}
                    {SECTIONS.filter(s => !u.effective_permissions?.[s.key]).map((section) => (
                      <span key={section.key} className="px-2 py-1 bg-red-500/10 text-red-400 rounded text-xs line-through opacity-50">
                        {section.label}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    {u.id !== user?.id && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(u);
                            setPermissionsDialogOpen(true);
                          }}
                        >
                          <Settings className="w-4 h-4 mr-1" />
                          Permissions
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(u);
                          }}
                        >
                          <Key className="w-4 h-4 mr-1" />
                          Credentials
                        </Button>
                        {!u.invite_accepted && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendInvite(u.id);
                            }}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Resend Invite
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(u);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </>
                    )}
                    {u.id === user?.id && (
                      <span className="text-sm text-muted-foreground italic">This is you</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Artists */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-green-500" />
          Artists ({artists.length})
        </h2>
        
        <div className="space-y-3">
          {artists.map((u) => (
            <div key={u.id} className="border border-border rounded-lg overflow-hidden">
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                onClick={() => setExpandedUsers({ ...expandedUsers, [u.id]: !expandedUsers[u.id] })}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-lg font-bold text-green-500">{u.name?.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-medium">{u.name}</p>
                    <p className="text-sm text-muted-foreground">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(u)}
                  {expandedUsers[u.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>
              
              {expandedUsers[u.id] && (
                <div className="px-4 pb-4 pt-2 border-t border-border bg-muted/30">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {SECTIONS.filter(s => u.effective_permissions?.[s.key]).map((section) => (
                      <span key={section.key} className="px-2 py-1 bg-green-500/10 text-green-600 rounded text-xs">
                        {section.label}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedUser(u);
                        setPermissionsDialogOpen(true);
                      }}
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Permissions
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditDialog(u);
                      }}
                    >
                      <Key className="w-4 h-4 mr-1" />
                      Credentials
                    </Button>
                    {!u.invite_accepted && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResendInvite(u.id);
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Resend Invite
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(u);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Edit Credentials Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) setEditForm({ id: '', name: '', email: '', password: '', role: '' });
      }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Edit User Credentials
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEditUser} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="User's name"
                required
              />
            </div>
            
            <div>
              <Label>Email (Login)</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="user@example.com"
                required
              />
            </div>
            
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Leave blank to keep current password"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Only fill this if you want to change the password
              </p>
            </div>
            
            <div>
              <Label>Role</Label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
              >
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="artist">Artist</option>
              </select>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invite New User
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleInvite} className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="John Smith"
                required
              />
            </div>
            
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="john@example.com"
                required
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-md"
              >
                <option value="manager">Manager</option>
                <option value="artist">Artist</option>
              </select>
            </div>
            
            <div>
              <Label className="mb-3 block">Section Access</Label>
              <div className="space-y-2 max-h-64 overflow-y-auto border border-border rounded-lg p-3">
                {SECTIONS.map((section) => (
                  <div key={section.key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div>
                      <p className="font-medium text-sm">{section.label}</p>
                      <p className="text-xs text-muted-foreground">{section.description}</p>
                    </div>
                    <Switch
                      checked={inviteForm.permissions[section.key] ?? (inviteForm.role === 'manager')}
                      onCheckedChange={() => toggleInvitePermission(section.key)}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                <Mail className="w-4 h-4 mr-2" />
                Send Invite
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={(open) => {
        setPermissionsDialogOpen(open);
        if (!open) setSelectedUser(null);
      }}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Edit Permissions for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-lg font-bold">{selectedUser?.name?.charAt(0)}</span>
              </div>
              <div>
                <p className="font-medium">{selectedUser?.name}</p>
                <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ml-auto ${getRoleBadgeColor(selectedUser?.role)}`}>
                {selectedUser?.role}
              </span>
            </div>
            
            <div>
              <Label className="mb-3 block">Section Access</Label>
              <div className="space-y-2 max-h-80 overflow-y-auto border border-border rounded-lg p-3">
                {SECTIONS.map((section) => {
                  const perms = selectedUser?.permissions || selectedUser?.effective_permissions || {};
                  return (
                    <div key={section.key} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                      <div>
                        <p className="font-medium text-sm">{section.label}</p>
                        <p className="text-xs text-muted-foreground">{section.description}</p>
                      </div>
                      <Switch
                        checked={perms[section.key] ?? false}
                        onCheckedChange={() => togglePermission(section.key)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleUpdatePermissions} className="flex-1">
                Save Permissions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setUserToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Remove User"
        itemName={userToDelete?.name}
        description="Are you sure you want to remove this user? They will lose access to HUG."
      />
    </div>
  );
};

export default UserManagement;
